// A project on its own page: figures for a period, today's timeline with the billed lane, rules, the weekly history
// and the last 14 days. Everything comes from one loaded range (the last eight weeks).
import { useMemo, useState, type ReactNode } from 'react';
import { mergeIntervals, reportBuckets, reportPerProject, ruleMatches, type Interval } from '../../../packages/core/src/index.ts';
import type { Project, ProjectRule } from '../api/types.ts';
import { dayRange, monthRange, weekRange } from '../lib/calendar.ts';
import { navigate } from '../lib/router.ts';
import { ProLock } from '../components/ProLock.tsx';
import { isOwner, billingProjects, client, displayTitle, group, providerInfo, reportItems, type RangeData } from '../model/data.ts';
import * as m from '../model/mutations.ts';
import { buildSnapshot, entryDays, timelineRange } from '../model/snapshot.ts';
import { Banner, EmptyState, Loading, Menu, MenuDivider, MenuItem, ScreenHeader, SectionHeader, Select, TextInput } from '../components/common.tsx';
import { RowButton } from '../components/pickers.tsx';
import { useFmt, useNow, useRange, useStore } from '../store/hooks.tsx';
import { Button, SegmentedControl, StatTile } from '../ui/ds.tsx';
import { Icon } from '../ui/Icon.tsx';
import { ActivityTimeline, type TimelineLane } from '../ui/timeline.tsx';
import { BarChart } from '../ui/chart.tsx';

type Period = 'today' | 'week' | 'month';

export function ProjectPage({ id }: { id: string }) {
  const store = useStore();
  const t = store.t;
  const now = useNow(1000);
  const project = store.ws.projects.find((p) => p.id === id && p.deletedAt === null);
  const history = useMemo(() => {
    const week = weekRange(now, store.timeZone, -7);
    return { start: week.start, end: dayRange(now, store.timeZone).end };
  }, [Math.floor(now / 60_000), store.timeZone]);
  const loaded = useRange(history, false);
  const back = <Button className="back" variant="plain" size="sm" icon="chevronLeft" onClick={() => navigate({ name: 'projects' })}>{t('Projekte')}</Button>;
  if (!project) {
    return (
      <div className="screen">
        <ScreenHeader back={back} title={t('Projekt')} />
        <EmptyState icon="folder" title={t('Projekt nicht gefunden')} message={t('Es wurde gelöscht.')}>
          <Button onClick={() => navigate({ name: 'projects' })}>{t('Zu den Projekten')}</Button>
        </EmptyState>
      </div>
    );
  }
  if (!loaded.data) {
    return <div className="screen"><ScreenHeader back={back} title={project.name} />{loaded.error ? <Banner tone="danger">{loaded.error}</Banner> : <Loading label={t('Lädt …')} />}</div>;
  }
  return <Page project={project} data={loaded.data} now={now} back={back} />;
}

function Page({ project, data, now, back }: { project: Project; data: RangeData; now: number; back: ReactNode }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const [period, setPeriod] = useState<Period>('week');
  const today = dayRange(now, store.timeZone);
  const range: Interval = period === 'today' ? today : period === 'week' ? weekRange(now, store.timeZone) : { start: monthRange(now, store.timeZone).start, end: today.end };
  const snap = buildSnapshot(store.ws, data, range, now);
  const totals = snap.byProject.get(project.id);
  const c = client(store.ws, project.clientId);
  const subtitle = [
    c?.name ?? t('Kein Kunde'),
    project.billable ? (project.rateCents !== null ? t('%@/h', fmt.money(project.rateCents, project.currency)) : t('Kein Satz')) : t('Intern · nicht abrechenbar'),
    project.roundingMinutes === 0 ? t('Export sekundengenau') : t('Export auf %lld min aufgerundet', project.roundingMinutes),
    ...(project.archivedAt !== null ? [t('archiviert')] : []),
  ].join(' · ');
  return (
    <div className="screen">
      <ScreenHeader back={back} title={project.name} subtitle={subtitle}>
        <SegmentedControl<Period> label={t('Zeitraum')} value={period} onChange={setPeriod}
          options={[{ value: 'today', label: t('Heute') }, { value: 'week', label: t('Woche') }, { value: 'month', label: t('Monat') }]} />
        {isOwner(store.ws) ? <Button icon="pencil" onClick={() => store.openSheet({ kind: 'project', projectId: project.id })}>{t('Bearbeiten')}</Button> : null}
        <Menu label={t('Mehr')} align="end" trigger={(props) => <Button variant="plain" iconOnly icon="more" aria-label={t('Mehr')} {...props} />}>
          {(menu) => (
            <>
              <MenuItem icon="share" onSelect={() => { menu.close(); store.openSheet({ kind: 'export', projectId: project.id }); }}>{t('CSV exportieren …')}</MenuItem>
              {store.pro
                ? <MenuItem icon="doc" onSelect={() => { menu.close(); store.openSheet({ kind: 'invoice', range }); }}>{t('Rechnungszusammenfassung …')}</MenuItem>
                : <MenuItem icon="doc" onSelect={() => { menu.close(); navigate({ name: 'pro' }); }}>{t('PDF und Rechnung mit Pro …')}</MenuItem>}
              {isOwner(store.ws) ? (
                <>
                  <MenuDivider />
                  {project.archivedAt === null
                    ? <MenuItem onSelect={() => { menu.close(); void store.apply(m.archiveProject(project, true, store.clock())); navigate({ name: 'projects' }); }}>{t('Archivieren')}</MenuItem>
                    : <MenuItem onSelect={() => { menu.close(); void store.apply(m.archiveProject(project, false, store.clock())); }}>{t('Aus dem Archiv holen')}</MenuItem>}
                </>
              ) : null}
            </>
          )}
        </Menu>
      </ScreenHeader>
      <div className="stats">
        <StatTile label={t('Abgerechnet')} value={fmt.hm(totals?.mergedMs ?? 0)} unit="h"
          hint={(totals?.overlapMs ?? 0) > 0 ? t('%@ h Summe · Überschneidung 1× gezählt', fmt.hm(totals?.rawMs ?? 0)) : t('Keine Überschneidung')} />
        <StatTile label={t('Eigene Zeit')} accent="human" value={fmt.hm(totals?.humanMs ?? 0)} unit="h" />
        <StatTile label={t('Agent-Zeit')} accent="agent" value={fmt.hm(totals?.agentMs ?? 0)} unit="h" />
        <StatTile label={t('Abrechenbar')} value={fmt.number((totals?.amountCents ?? 0) / 100)} unit="€"
          hint={project.billable ? t('Satz × zusammengeführte Zeit') : t('Internes Projekt')} />
      </div>
      <TodayLane project={project} data={data} now={now} />
      <Rules project={project} data={data} now={now} />
      <History project={project} data={data} now={now} />
      <Recent project={project} data={data} now={now} />
    </div>
  );
}

function TodayLane({ project, data, now }: { project: Project; data: RangeData; now: number }) {
  const store = useStore();
  const t = store.t;
  const today = dayRange(now, store.timeZone);
  const snap = buildSnapshot(store.ws, data, today, now);
  const lanes: TimelineLane[] = [];
  const entries = data.entries.filter((e) => e.projectId === project.id && e.startAt < today.end && (e.endAt ?? now) > today.start);
  if (entries.length > 0) {
    lanes.push({ id: 'me', label: t('Eigene Zeit'), source: 'human', segments: entries.map((e) => ({
      start: Math.max(e.startAt, today.start), end: Math.min(e.endAt ?? now, today.end), kind: 'counted' as const, ...(e.description ? { label: e.description } : {}),
    })) });
  }
  const bySession = group(snap.pieces.filter((p) => p.project === project.id), (p) => p.session);
  for (const [sessionId, list] of [...bySession].sort((a, b) => (a[1][0]?.start ?? 0) - (b[1][0]?.start ?? 0))) {
    const session = data.sessions.find((s) => s.id === sessionId);
    // The session's waiting stays visible (hatched, not counted) next to the time it counted for this project.
    const waiting = snap.sessions.find((s) => s.record.id === sessionId)?.waiting ?? [];
    lanes.push({
      id: sessionId, source: 'agent',
      label: (session ? providerInfo(session.provider).name : 'Agent') + (session?.repoName ? ` · ${session.repoName}` : ''),
      segments: [...mergeIntervals(list).map((i) => ({ ...i, kind: 'counted' as const })), ...waiting.map((i) => ({ ...i, kind: 'waiting' as const }))].sort((a, b) => a.start - b.start),
    });
  }
  return (
    <section className="tl-card panel">
      <SectionHeader title={t('Heute')} caption={t('Die Abrechnungszeile führt alle Quellen zusammen: Überschneidungen zählen einmal')} />
      {lanes.length === 0 ? <p className="note">{t('Heute noch keine Zeit in diesem Projekt.')}</p>
        : <ActivityTimeline {...timelineRange(snap)} now={now} lanes={lanes} merged label={t('Aktivität heute in %@', project.name)} />}
    </section>
  );
}

function Rules({ project, data, now }: { project: Project; data: RangeData; now: number }) {
  const store = useStore();
  const t = store.t;
  const [kind, setKind] = useState<'remote' | 'branch'>('remote');
  const [pattern, setPattern] = useState('');
  const rules = store.ws.rules.filter((r) => r.projectId === project.id);
  const since = now - 30 * 86_400_000;
  const recent = data.sessions.filter((s) => (s.lastEventAt ?? s.startedAt) >= since);
  const matches = (rule: ProjectRule) => recent.filter((s) => ruleMatches(rule, { remote: s.gitRemote, branch: s.gitBranch })).length;
  const add = () => {
    void store.apply(m.addRule(store.ws, project.id, kind, pattern, store.clock()));
    setPattern('');
  };
  return (
    <div className="stack">
      <SectionHeader title={t('Regeln für Agent-Sessions')} caption={t('Remote vor Branch; bei mehreren gewinnt die genauere Regel')} />
      <div className="tl-card rules">
        {rules.length === 0 ? <p className="note rules__empty">{t('Noch keine Regeln. Sessions aus passenden Repos oder Branches landen dann automatisch hier.')}</p> : null}
        {rules.map((rule) => (
          <div key={rule.id} className="rule">
            <Icon name={rule.kind === 'remote' ? 'network' : 'branch'} size={16} />
            <span className="rule__text">
              <span className="rule__pattern">{rule.pattern}</span>
              <span className="note">{`${rule.kind === 'remote' ? t('Git-Remote') : t('Branch-Muster')} · ${t('%lld Sessions in 30 Tagen', matches(rule))}`}</span>
            </span>
            {isOwner(store.ws) ? <Button variant="plain" size="sm" iconOnly icon="close" aria-label={t('Regel entfernen')} onClick={() => void store.apply(m.deleteRule(rule))} /> : null}
          </div>
        ))}
        {isOwner(store.ws) && !store.pro ? <div className="rule-add"><ProLock text={t('Regeln ordnen Sessions automatisch zu. Neue Regeln gibt es mit Pro.')} /></div> : null}
        {isOwner(store.ws) && store.pro ? <form className="rule-add" onSubmit={(e) => { e.preventDefault(); add(); }}>
          <Select value={kind} aria-label={t('Art')} onChange={(e) => setKind(e.target.value as 'remote' | 'branch')}>
            <option value="remote">{t('Git-Remote')}</option>
            <option value="branch">{t('Branch-Muster')}</option>
          </Select>
          <TextInput className="input--mono" value={pattern} aria-label={t('Muster')} placeholder={kind === 'remote' ? 'github.com/firma/repo' : 'kunde/*'}
            onChange={(e) => setPattern(e.target.value)} />
          <Button type="submit" size="sm" disabled={!pattern.trim()}>{t('Hinzufügen')}</Button>
        </form> : null}
        <p className="note note--faint rules__foot">{t('Ordner-Regeln legst du in der Mac-App an. Sie enthalten lokale Pfade und bleiben auf dem Mac.')}</p>
      </div>
    </div>
  );
}

function History({ project, data, now }: { project: Project; data: RangeData; now: number }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const [table, setTable] = useState(false);
  const items = reportItems(data, now).filter((i) => i.project === project.id);
  const weeks = reportBuckets(data.range, 'week', store.timeZone).map((week) => ({
    week, label: t('KW %lld', fmt.week(week.start)),
    totals: reportPerProject(items, billingProjects(store.ws).filter((p) => p.id === project.id), week, now).get(project.id),
  }));
  return (
    <section className="tl-card panel">
      <div className="sec-head">
        <h2>{t('Verlauf')}</h2>
        <SegmentedControl size="sm" label={t('Darstellung')} value={table ? 'table' : 'chart'} onChange={(v) => setTable(v === 'table')}
          options={[{ value: 'chart', label: t('Diagramm') }, { value: 'table', label: t('Tabelle') }]} />
      </div>
      {table ? (
        <div className="table__scroll">
          <div className="history-table">
            <div className="table__head history-grid"><span>{t('Woche')}</span><span className="table__num">{t('Abgerechnet')}</span><span className="table__num">{t('Summe')}</span>
              <span className="table__num">{t('Überschneidung')}</span><span className="table__num">{t('Betrag')}</span></div>
            {[...weeks].reverse().map((w) => (
              <div key={w.week.start} className="table__row history-grid">
                <span>{w.label}</span>
                <span className="table__num">{fmt.duration(w.totals?.mergedMs ?? 0)}</span>
                <span className="table__num table__num--muted">{fmt.duration(w.totals?.rawMs ?? 0)}</span>
                <span className="table__num table__num--muted">{fmt.duration(w.totals?.overlapMs ?? 0)}</span>
                <span className="table__num">{fmt.money(w.totals?.amountCents ?? 0, project.currency)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <BarChart height={200} label={t('Abgerechnete Stunden pro Woche')} groups={weeks.map((w) => ({
            key: String(w.week.start), label: w.label,
            bars: [{ key: 'billed', label: t('Abgerechnet'), value: w.totals?.mergedMs ?? 0, color: 'var(--ink)' }],
          }))} format={(ms) => fmt.duration(ms)} />
          <p className="note">{t('Abgerechnete Zeit pro Woche (zusammengeführt). Die Tabelle zeigt auch Summe, Überschneidung und Betrag.')}</p>
        </>
      )}
    </section>
  );
}

function Recent({ project, data, now }: { project: Project; data: RangeData; now: number }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const range = { start: dayRange(now, store.timeZone, -13).start, end: dayRange(now, store.timeZone).end };
  const days = entryDays(store.ws, data, range, now, store.timeZone);
  const entries = days.flatMap((d) => d.entries).filter((e) => e.projectId === project.id).sort((a, b) => b.startAt - a.startAt)
    .filter((e, i, list) => list.findIndex((x) => x.id === e.id) === i).slice(0, 8);
  const agents = days.flatMap((d) => d.agents).filter((b) => b.project?.id === project.id).sort((a, b) => b.start - a.start).slice(0, 8);
  const line = (key: string, title: string, sub: string, value: string, onClick: () => void) => (
    <RowButton key={key} onClick={onClick}>
      <span className="recent-line">
        <span className="recent-line__text"><span className="recent-line__title">{title}</span><span className="note">{sub}</span></span>
        <span className="num-cell">{value}</span>
      </span>
    </RowButton>
  );
  return (
    <div className="stack">
      <SectionHeader title={t('Letzte 14 Tage')} />
      <div className="cols">
        <div className="tl-card recent">
          <div className="tl-label recent__label">{t('Eigene Einträge')}</div>
          {entries.length === 0 ? <p className="note recent__empty">{t('Keine Einträge')}</p> : entries.map((e) =>
            line(e.id, e.description || t('Ohne Beschreibung'), fmt.started(e.startAt, now), fmt.duration((e.endAt ?? now) - e.startAt),
              () => store.openSheet({ kind: 'entry', entryId: e.id, day: null })))}
        </div>
        <div className="tl-card recent">
          <div className="tl-label recent__label">{t('Agent-Sessions')}</div>
          {agents.length === 0 ? <p className="note recent__empty">{t('Keine Agent-Zeit')}</p> : agents.map((b) =>
            line(b.id, displayTitle(b.session), `${providerInfo(b.session.provider).name} · ${fmt.started(b.start, now)}`, fmt.duration(b.countedMs),
              () => navigate({ name: 'session', id: b.session.id })))}
        </div>
      </div>
    </div>
  );
}
